/**
 * The First hire guide (piece 34, *The Hire window*; decision 6842): four
 * steps the Hire window draws above its sections while any stands undone, each
 * read done from the installation's own rows and each a link to where it is
 * done. 🔒 Nothing here is stored, and the guide seeds nothing.
 */

/** The facts, behind `hr.hire` as the window's own reads are. */
export const FIRST_HIRE_STEPS_PATH = '/hr/hire/setup-steps';

export const FIRST_HIRE_STEPS = ['domain', 'site', 'jobRole', 'rules'] as const;
export type FirstHireStep = (typeof FIRST_HIRE_STEPS)[number];

/** What the server reads; every field is a saved row's fact. */
export interface FirstHireStepsView {
  /** A registered email domain beyond `localhost`, the current one where it is; null while only `localhost` is registered. */
  domain: string | null;
  /** Our own Company, whose page a site is added on. */
  ownCompanyId: string | null;
  /** The sites of our own Company. */
  ownSites: number;
  /** Active job roles the window's Job section offers. */
  activeJobRoles: number;
  /** The states of our own sites with no employment rules on file, each once. */
  statesWithoutRules: string[];
  /** Our own sites whose address carries no state, so no rules can resolve. */
  sitesWithoutState: number;
}

export interface FirstHireStepFacts {
  done: boolean;
  /** Whether what the step needs is done; a step that cannot be reached draws as later. */
  reachable: boolean;
}

/**
 * 🔒 Each step's done test (decision 6842). The domain is registered when one
 * beyond `localhost` is on file; the site when our own Company has one; the job
 * role when one is active; the rules when every site of ours sits in a state
 * with a row, which waits for a site. A key the answer lacks reads as not done.
 */
export function firstHireSteps(view: Partial<FirstHireStepsView>): Record<FirstHireStep, FirstHireStepFacts> {
  const sites = (view.ownSites ?? 0) > 0;
  return {
    domain: { done: Boolean(view.domain), reachable: true },
    site: { done: sites, reachable: true },
    jobRole: { done: (view.activeJobRoles ?? 0) > 0, reachable: true },
    rules: { done: sites && Array.isArray(view.statesWithoutRules) && view.statesWithoutRules.length === 0 && (view.sitesWithoutState ?? 0) === 0, reachable: sites },
  };
}

/** Whether every step is done, which hides the guide from the window. */
export function firstHireReady(view: Partial<FirstHireStepsView>): boolean {
  return Object.values(firstHireSteps(view)).every((step) => step.done);
}
